import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { recentAccountCurve, recentRange } from '@/utils/accountCurve';
import { tradingSummary } from '@/utils/tradingSummary';
import CountUp from '@/components/base/CountUp';
import Reveal from '@/components/base/Reveal';
import BalanceTooltip from '@/components/feature/BalanceTooltip';

const formatMoney = (n: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatSignedPercent = (n: number): string => `${n < 0 ? '-' : '+'}${Math.abs(n).toFixed(1)}%`;

// Derive headline balances from the same 30-day series the chart renders, so they can never drift.
const balances = recentAccountCurve.map((d) => d.balance);
const startBalance = balances[0];
const finalBalance = balances[balances.length - 1];
const balanceMin = Math.min(...balances);
const balanceMax = Math.max(...balances);
const balancePad = Math.max((balanceMax - balanceMin) * 0.15, 20);
const balanceDomain: [number, number] = [balanceMin - balancePad, balanceMax + balancePad];

// Net change across the month: month open → month close, as money and as a percentage.
const netChange = finalBalance - startBalance;
const netChangePercent = startBalance !== 0 ? (netChange / startBalance) * 100 : 0;

type Tone = 'profit' | 'loss' | 'neutral';

const toneClass: Record<Tone, string> = {
  profit: 'text-profit-600',
  loss: 'text-loss-500',
  neutral: 'text-foreground-900',
};

const netTone: Tone = netChange >= 0 ? 'profit' : 'loss';

// Net change is the headline up top; the grid keeps the plain balance readouts.
const readout: { label: string; value: string; tone: Tone }[] = [
  { label: 'Month Open', value: formatMoney(startBalance), tone: 'neutral' },
  { label: 'Month Close', value: formatMoney(finalBalance), tone: 'neutral' },
  { label: 'Period', value: `${recentRange.from} → ${recentRange.to}`, tone: 'neutral' },
];

function PerformanceCurve() {
  return (
    <section className="w-full border-y border-background-200 bg-background-100 px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                ACCOUNT HISTORY
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px]">
                The Last Thirty Days, Unsmoothed
              </h2>
              <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-foreground-600 max-w-[600px]">
                The most recent month of the balance, kept as the real sequence of results —
                drawdowns included, nothing smoothed away.
              </p>
            </div>
            <div className="lg:text-right lg:pb-2">
              <p className="text-[12px] font-data tracking-[0.2em] text-foreground-400">
                NET CHANGE · LAST 30 DAYS
              </p>
              <p
                className={`mt-2 flex items-center gap-2 lg:justify-end font-data text-[34px] md:text-[46px] font-semibold leading-none tabular-nums ${toneClass[netTone]}`}
              >
                <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center" aria-hidden="true">
                  <i
                    className={`${netChange >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[30px] md:text-[40px]`}
                  ></i>
                </span>
                <CountUp
                  value={netChange}
                  decimals={2}
                  prefix={netChange >= 0 ? '+' : ''}
                />
              </p>
              <p className={`mt-3 font-data text-[13px] md:text-[14px] ${toneClass[netTone]}`}>
                {formatSignedPercent(netChangePercent)} since month open
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-10 rounded-lg border border-background-300 bg-background-50 p-4 md:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {readout.map((item) => (
                <div
                  key={item.label}
                  className="rounded-md border border-background-200 bg-background-100 px-4 py-3 flex items-center justify-between gap-3"
                >
                  <span className="text-[11px] text-foreground-500 whitespace-nowrap">{item.label}</span>
                  <span
                    className={`text-[12px] font-data whitespace-nowrap ${toneClass[item.tone]}`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            <div className="h-[240px] md:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={recentAccountCurve} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="strategyBalanceFade" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.16} />
                      <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="oklch(var(--foreground-950) / 0.06)" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 11 }}
                    axisLine={{ stroke: 'oklch(var(--foreground-950) / 0.08)' }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={balanceDomain}
                    tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <Tooltip
                    content={<BalanceTooltip />}
                    cursor={{ stroke: 'oklch(var(--foreground-950) / 0.18)', strokeWidth: 1 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="oklch(var(--foreground-800))"
                    strokeWidth={2}
                    fill="url(#strategyBalanceFade)"
                    dot={false}
                    activeDot={{
                      r: 4,
                      fill: 'oklch(var(--foreground-800))',
                      stroke: 'oklch(var(--background-50))',
                      strokeWidth: 2,
                    }}
                    isAnimationActive
                    animationDuration={2000}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-[11px] font-data tracking-wider text-foreground-400">
              {recentRange.from} → {recentRange.to} · {tradingSummary.instrument} · LAST 30 DAYS
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default PerformanceCurve;